import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Play, RotateCcw, Lightbulb, CheckCircle, ExternalLink, BookOpen } from 'lucide-react';
import { Button, CodeEditor, OutputPanel, Modal, LoadingSpinner, ExecutionPlanModal } from '../components';
import { useProgress } from '../hooks/useProgress';
import { usePyodide } from '../hooks/usePyodide';
import challengesData from '../data/challenges.json';

export default function Challenge() {
  const { topicId, challengeId } = useParams();
  const navigate = useNavigate();
  const { runMCodeWithTrace, isReady, isLoading: pyodideLoading } = usePyodide();
  const {
    completeChallenge,
    isChallengeCompleted,
    saveUserCode,
    getUserCode,
  } = useProgress();

  const [code, setCode] = useState('');
  const [result, setResult] = useState(null);
  const [structured, setStructured] = useState(null);
  const [error, setError] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [justCompleted, setJustCompleted] = useState(false);
  const [lastRunCorrect, setLastRunCorrect] = useState(null);
  const [traceData, setTraceData] = useState(null);
  const [showExecutionPlan, setShowExecutionPlan] = useState(false);

  // Track current challenge to prevent saving to wrong challenge during navigation
  const currentChallengeRef = useRef({ topicId, challengeId });
  // Track the latest code value for Cmd+Enter
  const codeRef = useRef(code);

  // Find current challenge
  const topic = challengesData.topics.find(w => w.id === topicId);
  const challenges = challengesData.challenges[topicId] || [];
  const challengeIndex = challenges.findIndex(c => c.id === challengeId);
  const challenge = challenges[challengeIndex];

  const prevChallenge = challengeIndex > 0 ? challenges[challengeIndex - 1] : null;
  const nextChallenge = challengeIndex < challenges.length - 1 ? challenges[challengeIndex + 1] : null;

  // Use either context value OR local justCompleted state (for immediate feedback)
  const isCompleted = isChallengeCompleted(topicId, challengeId) || justCompleted;

  // Wrapper to update both code state and codeRef synchronously
  const handleCodeChange = useCallback((newCode) => {
    codeRef.current = newCode;
    setCode(newCode);
  }, []);

  // Load saved code when challenge changes (only on topicId/challengeId change)
  useEffect(() => {
    // Find challenge data directly in effect to avoid dependency on object reference
    const currentChallenges = challengesData.challenges[topicId] || [];
    const currentChallenge = currentChallenges.find(c => c.id === challengeId);

    if (currentChallenge) {
      // Update ref to current challenge
      currentChallengeRef.current = { topicId, challengeId };

      // Load saved code or starter code
      const savedCode = getUserCode(topicId, challengeId);
      const newCode = savedCode || currentChallenge.starter_code || '';
      codeRef.current = newCode;
      setCode(newCode);
      setResult(null);
      setStructured(null);
      setError(null);
      setJustCompleted(false);
      setLastRunCorrect(null);
      setTraceData(null);
      setShowExecutionPlan(false);
    }
    // Note: getUserCode is intentionally excluded from deps to prevent result clearing
    // when progress changes (e.g., after completing a challenge)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicId, challengeId]);

  // Save code when it changes (debounced and only for current challenge)
  useEffect(() => {
    if (code === undefined || !challenge) return;

    // Only save if this is still the current challenge (prevents race conditions)
    const { topicId: currentTopic, challengeId: currentChallenge } = currentChallengeRef.current;
    if (currentTopic !== topicId || currentChallenge !== challengeId) return;

    // Debounce saves to avoid too many writes
    const timeoutId = setTimeout(() => {
      // Double-check we're still on the same challenge
      if (currentChallengeRef.current.topicId === topicId &&
          currentChallengeRef.current.challengeId === challengeId) {
        saveUserCode(topicId, challengeId, code);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [code, topicId, challengeId, saveUserCode, challenge]);


  const handleRun = useCallback(async (passedCode) => {
    if (!isReady || isRunning) return;

    // Use passed code if available (from Cmd+Enter), otherwise use codeRef
    // Check that passedCode is a string (not an event object from button click)
    const currentCode = (typeof passedCode === 'string') ? passedCode : codeRef.current;

    // Check for empty code
    const trimmedCode = currentCode.trim();
    if (!trimmedCode) {
      setError('Please enter some code first');
      return;
    }

    // Check if code is only comments
    const codeWithoutComments = trimmedCode
      .split('\n')
      .filter(line => !line.trim().startsWith('//'))
      .join('\n')
      .trim();

    if (!codeWithoutComments) {
      setError('Your code contains only comments. Add an expression to evaluate.');
      return;
    }

    setIsRunning(true);
    setResult(null);
    setStructured(null);
    setError(null);
    setTraceData(null);

    try {
      const response = await runMCodeWithTrace(trimmedCode);

      // Always store trace data when available
      if (response.trace) {
        setTraceData(response.trace);
      }

      if (response.success) {
        setResult(response.result);
        setStructured(response.structured || null);

        // Check if correct
        const expectedStr = formatExpectedOutput(challenge.expected_output);
        const isCorrect = response.result === expectedStr;
        setLastRunCorrect(isCorrect);

        if (isCorrect) {
          // Complete the challenge
          completeChallenge(topicId, challengeId, 3, challenges.length);
          setJustCompleted(true);
        }
      } else {
        // Check if this challenge expects an error
        const expectedStr = typeof challenge.expected_output === 'string' ? challenge.expected_output : '';
        const expectsError = expectedStr.startsWith('[Error]');

        if (expectsError) {
          // Format the error result to match expected format
          const errorResult = `[Error] ${response.error}`;
          setResult(errorResult);
          setStructured({ kind: 'scalar', type: 'error', value: response.error });

          const isCorrect = errorResult === expectedStr;
          setLastRunCorrect(isCorrect);

          if (isCorrect) {
            completeChallenge(topicId, challengeId, 3, challenges.length);
            setJustCompleted(true);
          }
        } else {
          setError(response.error);
          setLastRunCorrect(false);
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsRunning(false);
    }
  }, [isReady, isRunning, runMCodeWithTrace, challenge, completeChallenge, topicId, challengeId, challenges.length]);

  const handleReset = () => {
    const resetCode = challenge?.starter_code || '';
    codeRef.current = resetCode;
    setCode(resetCode);
    setResult(null);
    setStructured(null);
    setError(null);
    setLastRunCorrect(null);
  };

  const handleNextChallenge = () => {
    if (nextChallenge) {
      navigate(`/challenge/${topicId}/${nextChallenge.id}`);
    }
  };

  if (!challenge) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-game-text-dim mb-4">Challenge not found</p>
          <Link to={`/topic/${topicId}`}>
            <Button>Back to Topic</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (pyodideLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner message="Loading Python runtime..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col overflow-y-auto">
      {/* Header */}
      <header className="p-4 border-b border-[#2a2d31] flex-shrink-0">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to={`/topic/${topicId}`}>
              <Button variant="ghost" size="sm" pill={false}>
                <ArrowLeft size={18} />
              </Button>
            </Link>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-lg font-bold text-game-text">{challenge.title}</h1>
                {isCompleted && <CheckCircle size={16} className="text-game-primary" />}
              </div>
              <p className="text-sm text-game-text-dim">{topic?.name} • Challenge {challengeIndex + 1}</p>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center gap-2">
            {prevChallenge && (
              <Link to={`/challenge/${topicId}/${prevChallenge.id}`}>
                <Button variant="ghost" size="sm" pill={false}>
                  <ArrowLeft size={16} />
                </Button>
              </Link>
            )}
            <span className="text-sm text-game-text-dim font-medium">
              {challengeIndex + 1} / {challenges.length}
            </span>
            {nextChallenge && isChallengeCompleted(topicId, challengeId) && (
              <Link to={`/challenge/${topicId}/${nextChallenge.id}`}>
                <Button variant="ghost" size="sm" pill={false}>
                  <ArrowRight size={16} />
                </Button>
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 max-w-6xl mx-auto w-full p-6 pb-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left column: Instructions, Code input, Buttons */}
          <div className="flex flex-col gap-4">
            {/* Instructions - order 1 on mobile */}
            <div className="p-5 bg-game-surface rounded-2xl border border-[#2a2d31] order-1">
              <h2 className="font-bold text-game-text mb-3">Instructions</h2>
              <p className="text-game-text-dim whitespace-pre-line">{challenge.description}</p>
              {challenge.doc_url && (
                <a
                  href={challenge.doc_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 mt-3 text-sm text-game-primary hover:text-game-accent transition-colors"
                >
                  <ExternalLink size={14} />
                  Microsoft Docs
                </a>
              )}
            </div>

            {/* Expected output - visible on mobile only, order 2 */}
            <div className="p-5 bg-game-surface rounded-2xl border border-[#2a2d31] lg:hidden order-2">
              <h2 className="font-bold text-game-text mb-3">Expected Output</h2>
              <pre className="text-game-primary font-mono text-sm bg-[#2a2d31] p-3 rounded-xl overflow-x-auto">
                {formatExpectedOutput(challenge.expected_output)}
              </pre>
            </div>

            {/* Code editor - order 3 on mobile */}
            <div className="order-3 bg-game-surface rounded-2xl border border-[#2a2d31] overflow-hidden">
              <div className="flex items-center justify-between px-4 h-[46px] bg-[#2a2d31]/50 border-b border-[#2a2d31]">
                <span className="text-sm font-semibold text-game-text">Your Code</span>
                <button
                  onClick={handleReset}
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium text-game-text-dim hover:text-game-text hover:bg-[#2a2d31] transition-colors"
                >
                  <RotateCcw size={12} />
                  Reset
                </button>
              </div>
              <CodeEditor
                value={code}
                onChange={handleCodeChange}
                onRun={handleRun}
                height={window.innerWidth >= 1024 ? '500px' : window.innerWidth >= 768 ? '450px' : '300px'}
              />
            </div>

            {/* Buttons - order 4 on mobile */}
            <div className="flex gap-3 order-4">
              <Button
                onClick={handleRun}
                disabled={!isReady || isRunning}
                variant={isCompleted ? "secondary" : "primary"}
                className={`${isCompleted ? "flex-1" : "w-full"} ${justCompleted ? "btn-split-left" : ""}`}
              >
                <Play size={18} className="mr-2" />
                {isRunning ? 'Running...' : 'Run Code'}
              </Button>
              {isCompleted && nextChallenge && (
                <Button
                  onClick={handleNextChallenge}
                  className={`flex-1 ${justCompleted ? "btn-split-right" : ""}`}
                >
                  Next Challenge
                  <ArrowRight size={18} className="ml-2" />
                </Button>
              )}
              {isCompleted && !nextChallenge && (
                <Link to={`/topic/${topicId}`} className={`flex-1 ${justCompleted ? "btn-split-right" : ""}`}>
                  <Button className="w-full">
                    Finish Topic
                    <ArrowRight size={18} className="ml-2" />
                  </Button>
                </Link>
              )}
            </div>

            {/* Hint button - visible on mobile only, order 5 */}
            {challenge.hints && challenge.hints.length > 0 && (
              <Button
                variant="secondary"
                onClick={() => setShowHint(true)}
                className="w-full lg:hidden order-5"
              >
                <Lightbulb size={18} className="mr-2" />
                Show Hint
              </Button>
            )}

            {/* Output panel - visible on mobile only, order 6 */}
            <div className="lg:hidden order-6">
              <OutputPanel
                result={result}
                structured={structured}
                error={error}
                isRunning={isRunning}
                isCorrect={lastRunCorrect}
                onShowExecutionPlan={traceData ? () => setShowExecutionPlan(true) : null}
                isTracing={false}
              />
            </div>

            {/* Learn section - mobile only, order 7 */}
            {isCompleted && (challenge.explanation || challenge.alternatives) && (
              <div className="p-5 bg-game-surface rounded-2xl border border-[#2a2d31] lg:hidden order-7">
                <h2 className="font-bold text-game-primary mb-3 flex items-center gap-2">
                  <BookOpen size={18} />
                  Learn
                </h2>
                {challenge.explanation && (
                  <p className="text-game-text-dim text-sm mb-4">{challenge.explanation}</p>
                )}
                {challenge.alternatives && challenge.alternatives.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-game-text mb-2">Other ways to solve:</h3>
                    <div className="space-y-2">
                      {challenge.alternatives.map((alt, i) => (
                        <div key={i} className="bg-[#2a2d31] rounded-xl p-3">
                          <pre className="text-game-primary font-mono text-xs overflow-x-auto whitespace-pre-wrap">{alt.code}</pre>
                          {alt.note && (
                            <p className="text-xs text-game-text-dim mt-1.5">{alt.note}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right column: Expected Output, Output, Show Hint - desktop only */}
          <div className="hidden lg:block space-y-4">
            {/* Expected output */}
            <div className="p-5 bg-game-surface rounded-2xl border border-[#2a2d31]">
              <h2 className="font-bold text-game-text mb-3">Expected Output</h2>
              <pre className="text-game-primary font-mono text-sm bg-[#2a2d31] p-3 rounded-xl overflow-x-auto">
                {formatExpectedOutput(challenge.expected_output)}
              </pre>
            </div>

            {/* Output panel */}
            <OutputPanel
              result={result}
              structured={structured}
              error={error}
              isRunning={isRunning}
              isCorrect={lastRunCorrect}
              onShowExecutionPlan={traceData ? () => setShowExecutionPlan(true) : null}
              isTracing={false}
            />

            {/* Hint button */}
            {challenge.hints && challenge.hints.length > 0 && (
              <Button
                variant="secondary"
                onClick={() => setShowHint(true)}
                className="w-full"
              >
                <Lightbulb size={18} className="mr-2" />
                Show Hint
              </Button>
            )}

            {/* Learn section - desktop */}
            {isCompleted && (challenge.explanation || challenge.alternatives) && (
              <div className="p-5 bg-game-surface rounded-2xl border border-[#2a2d31]">
                <h2 className="font-bold text-game-primary mb-3 flex items-center gap-2">
                  <BookOpen size={18} />
                  Learn
                </h2>
                {challenge.explanation && (
                  <p className="text-game-text-dim text-sm mb-4">{challenge.explanation}</p>
                )}
                {challenge.alternatives && challenge.alternatives.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-game-text mb-2">Other ways to solve:</h3>
                    <div className="space-y-2">
                      {challenge.alternatives.map((alt, i) => (
                        <div key={i} className="bg-[#2a2d31] rounded-xl p-3">
                          <pre className="text-game-primary font-mono text-xs overflow-x-auto whitespace-pre-wrap">{alt.code}</pre>
                          {alt.note && (
                            <p className="text-xs text-game-text-dim mt-1.5">{alt.note}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Hint modal */}
      <Modal
        isOpen={showHint}
        onClose={() => setShowHint(false)}
        title="Hint"
        size="md"
      >
        <div className="space-y-3">
          {challenge.hints?.map((hint, i) => (
            <div key={i} className="p-4 bg-[#2a2d31] rounded-xl">
              <p className="text-game-text-dim">{hint}</p>
            </div>
          ))}
        </div>
      </Modal>

      {/* Execution Plan modal */}
      <ExecutionPlanModal
        isOpen={showExecutionPlan}
        onClose={() => setShowExecutionPlan(false)}
        trace={traceData}
        isLoading={false}
      />

    </div>
  );
}

// Check if a string looks like a date/time/duration value (should not be quoted)
function isDateTimeString(str) {
  // Date: 2024-01-15
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return true;
  // Time: 14:30:00
  if (/^\d{2}:\d{2}:\d{2}$/.test(str)) return true;
  // DateTime: 2024-01-15T10:30:00
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(str)) return true;
  // Duration: 2.05:00:00
  if (/^\d+\.\d{2}:\d{2}:\d{2}$/.test(str)) return true;
  // DateTimeZone: 2024-01-15T10:30:00+05:00
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}[+-]\d{2}:\d{2}$/.test(str)) return true;
  return false;
}

// Helper to format expected output for display
function formatExpectedOutput(value) {
  if (value === null) return 'null';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'number') {
    return Number.isInteger(value) ? value.toString() : value.toString();
  }
  if (typeof value === 'string') {
    // Don't quote date/time/duration values
    if (isDateTimeString(value)) return value;
    // Don't quote error values
    if (value.startsWith('[Error]')) return value;
    return `"${value}"`;
  }
  if (Array.isArray(value)) {
    const items = value.map(formatExpectedOutput);
    return '{' + items.join(', ') + '}';
  }
  if (typeof value === 'object') {
    const fields = Object.entries(value).map(([k, v]) => `${k} = ${formatExpectedOutput(v)}`);
    return '[' + fields.join(', ') + ']';
  }
  return String(value);
}
